import { Inline, Stack } from '@/ui/line/line';
import { Logo } from '@/ui/logo/logo';
import { styled } from 'goober';
import { SkeletonWidget } from '../../overview/sidebar/project-sidebar';
import type { CompanyData } from '@/api/v4/organization.api';
import { colors } from '@/theme/colors';
import {
  WIDGET_CONTAINER_PADDING_HORIZONTAL,
  WIDGET_CONTAINER_PADDING_VERTICAL,
} from '@/ui/widget/widget';
import { Header, SubHeaderBold } from '@/ui/typography/widgets';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { isJSON } from '@/helpers/is-json';
import { draftJsRawToPlainText } from '@/helpers/draftjs';
import { Small } from '@/ui/typography/across-platform';

const WIDGET_WIDTH = 800;

export const ProjectListingDetails = ({
  companyName,
  logoUrl,
  description,
  keyOffering,
  isDataLoading,
}: {
  companyName?: CompanyData['name'];
  logoUrl?: CompanyData['logoUrl'];
  description?: CompanyData['detailedDescription'];
  keyOffering?: CompanyData['keyOffering'];
  isDataLoading: boolean;
}) => {
  const { t } = useTranslation('projects', { keyPrefix: 'listingSidebar' });

  const formattedDescription = useMemo(() => {
    const val = description?.value ?? '';
    if (!val) return '';
    return isJSON(val) ? draftJsRawToPlainText(val) : val;
  }, [description?.value]);

  if (isDataLoading) {
    return <SkeletonWidget width={WIDGET_WIDTH} />;
  }

  return (
    <Container>
      <Inline gap="12px" alignItems="center" justifyContent="flex-start">
        <CompanyLogo
          name={companyName?.value ?? ''}
          logoUrl={logoUrl?.value}
          bgColor={logoUrl?.value ? 'transparent' : colors.accent.green.c3}
          borderColor={logoUrl?.value ? colors.gray.c3 : undefined}
        />
        <Header>{companyName?.value}</Header>
      </Inline>
      {formattedDescription && <Small>{formattedDescription}</Small>}
      {keyOffering?.value && (
        <>
          <SubHeaderBold>{t`keyOffering`}</SubHeaderBold>
          <Small>{keyOffering?.value}</Small>
        </>
      )}
    </Container>
  );
};

const Container = styled(Stack)`
  background-color: ${({ theme }) => theme.colors.basics.white};
  padding: ${WIDGET_CONTAINER_PADDING_VERTICAL}px
    ${WIDGET_CONTAINER_PADDING_HORIZONTAL}px;
  border-radius: 20px;
  min-width: ${WIDGET_WIDTH}px;
  max-width: ${WIDGET_WIDTH}px;
`;

const CompanyLogo = styled(Logo)<{ borderColor?: string }>`
  ${({ theme }) => theme.typography.acrossPlatform.fullSizedHeader}
  color: ${({ theme }) => theme.colors.basics.white};
  width: 80px;
  height: 80px;
  letter-spacing: normal;
  border: 2px solid
    ${({ borderColor }) => (borderColor ? borderColor : 'transparent')};
`;
